import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import {
  formatFiscalYear,
  formatRegistration,
} from "@/components/company/shared/company-side-panel-helpers";
import FieldLabel from "@/components/company/shared/field-label";
import InlineCopyValue from "@/components/company/shared/inline-copy-value";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import { hasLegalIdentityData } from "@/lib/company-data-availability";
import { toHumanReadableAmount } from "@/lib/string";
import { CompanyDTO } from "@shaarp/common";
import { FileTextIcon, ScrollTextIcon } from "lucide-react";
import { type ReactNode, useState } from "react";

const CORPORATE_OBJECT_MAX_LENGTH = 150;

type IdentityFieldProps = {
  label: string;
  children: ReactNode;
  spanFull?: boolean;
};

function IdentityField({ label, children, spanFull }: IdentityFieldProps) {
  return (
    <div
      className={`flex flex-col gap-1 min-w-0 ${spanFull ? "col-span-full" : ""}`}
    >
      <FieldLabel>{label}</FieldLabel>
      <span className="text-sm font-semibold break-words">{children}</span>
    </div>
  );
}

function EmptyValue() {
  return <span className="text-muted-foreground font-normal">—</span>;
}

const CERTIFICATION_FLAG_KEYS: {
  key: string;
  get: (c: CompanyDTO) => boolean | null | undefined;
}[] = [
  { key: "qualiopi", get: (c) => c.certifications?.isQualiopi },
  { key: "rge", get: (c) => c.certifications?.isRGE },
  { key: "bio", get: (c) => c.certifications?.isBio },
  {
    key: "trainingOrganization",
    get: (c) => c.certifications?.isTrainingOrganization,
  },
  { key: "siae", get: (c) => c.certifications?.isSIAE },
  { key: "ess", get: (c) => c.business?.isESSCompany },
  { key: "missionDriven", get: (c) => c.business?.isMissionDriven },
];

function getCertificationBadges(
  company: CompanyDTO,
  t: (key: string) => string,
): string[] {
  return CERTIFICATION_FLAG_KEYS.filter(({ get }) => get(company)).map(
    ({ key }) => t(`company.insights.certifications.${key}`),
  );
}

type CorporateObjectProps = {
  text: string;
};

function CorporateObject({ text }: CorporateObjectProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > CORPORATE_OBJECT_MAX_LENGTH;

  const displayText =
    !expanded && isLong
      ? `${text.slice(0, CORPORATE_OBJECT_MAX_LENGTH)}...`
      : text;

  return (
    <div className="flex items-start gap-2 font-normal text-sm">
      <FileTextIcon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <p className="text-foreground break-words">
        {displayText}
        {isLong && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="ml-1 text-xs font-medium cursor-pointer text-primary hover:underline"
            >
              {expanded
                ? t("company.showMoreLess.showLess")
                : t("company.showMoreLess.showMore_zero")}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

type CardCompanyLegalIdentityProps = {
  readonly company: CompanyDTO;
  /**
   * When true, render every standard identity field even if empty (with a
   * placeholder "—"). Used on the administrative tab to keep the page
   * meaningful for companies whose SIRET could not be reliably identified.
   */
  readonly showAllFields?: boolean;
};

export default function CardCompanyLegalIdentity({
  company,
  showAllFields = false,
}: CardCompanyLegalIdentityProps) {
  const { t, currentLanguage } = useTranslation();
  const [copiedValue, copyToClipboard] = useCopyToClipboard();

  const business = company.business;
  const hasIdentityFields = hasLegalIdentityData(company);
  const certificationBadges = getCertificationBadges(company, t);
  const hasCertifications = certificationBadges.length > 0;

  if (!hasIdentityFields && !hasCertifications && !showAllFields) return null;

  const hasSiren = !!(business.sirenFormatted || business.siren);
  const hasSiret = !!(business.siretFormatted || business.siret);
  const hasTvaIntra = !!business.tvaIntra;
  const hasCapital = business.capital != null;
  const hasNaf = !!business.nafCode && !!business.nafLabel;
  const hasCollectiveAgreements =
    (business.collectiveAgreements?.length ?? 0) > 0;
  const hasCorporateObject = (business.corporateObject?.length ?? 0) > 0;
  const registration = formatRegistration(business, t, currentLanguage);
  const fiscalYear = formatFiscalYear(business, t, currentLanguage);

  const showIdentityGrid = hasIdentityFields || showAllFields;

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon
          icon={ScrollTextIcon}
          title={t("company.insights.identity.title")}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {showIdentityGrid && (
          <div className="grid grid-cols-1 @sm:grid-cols-2 @xl:grid-cols-3 gap-x-6 gap-y-4">
            {hasSiren ? (
              <IdentityField label={t("company.insights.identity.siren")}>
                <InlineCopyValue
                  value={business.sirenFormatted ?? business.siren!}
                  copyValue={business.siren ?? business.sirenFormatted!}
                  copiedValue={copiedValue}
                  onCopy={copyToClipboard}
                />
              </IdentityField>
            ) : (
              showAllFields && (
                <IdentityField label={t("company.insights.identity.siren")}>
                  <EmptyValue />
                </IdentityField>
              )
            )}

            {hasSiret ? (
              <IdentityField label={t("company.insights.identity.siret")}>
                <InlineCopyValue
                  value={business.siretFormatted ?? business.siret!}
                  copyValue={business.siret ?? business.siretFormatted!}
                  copiedValue={copiedValue}
                  onCopy={copyToClipboard}
                />
              </IdentityField>
            ) : (
              showAllFields && (
                <IdentityField label={t("company.insights.identity.siret")}>
                  <EmptyValue />
                </IdentityField>
              )
            )}

            {hasTvaIntra ? (
              <IdentityField label={t("company.insights.identity.tvaIntra")}>
                <InlineCopyValue
                  value={business.tvaIntra!}
                  copyValue={business.tvaIntra!}
                  copiedValue={copiedValue}
                  onCopy={copyToClipboard}
                />
              </IdentityField>
            ) : (
              showAllFields && (
                <IdentityField label={t("company.insights.identity.tvaIntra")}>
                  <EmptyValue />
                </IdentityField>
              )
            )}

            {hasCapital ? (
              <IdentityField label={t("company.insights.capital")}>
                {toHumanReadableAmount(business.capital!)}
              </IdentityField>
            ) : (
              showAllFields && (
                <IdentityField label={t("company.insights.capital")}>
                  <EmptyValue />
                </IdentityField>
              )
            )}

            {hasNaf ? (
              <IdentityField label={t("company.insights.identity.nafCode")}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="cursor-pointer">
                      {business.nafCode}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {business.nafCode} - {business.nafLabel}
                  </TooltipContent>
                </Tooltip>
              </IdentityField>
            ) : (
              showAllFields && (
                <IdentityField label={t("company.insights.identity.nafCode")}>
                  <EmptyValue />
                </IdentityField>
              )
            )}

            {business.enterpriseCategory && (
              <IdentityField
                label={t("company.insights.enterpriseCategory")}
              >
                {business.enterpriseCategory}
              </IdentityField>
            )}

            {registration && (
              <IdentityField label={t("company.insights.identity.registration")}>
                {registration}
              </IdentityField>
            )}

            {fiscalYear && (
              <IdentityField label={t("company.insights.identity.fiscalYear")}>
                {fiscalYear}
              </IdentityField>
            )}

            {business.hasConfidentialAccounts && (
              <IdentityField label={t("company.insights.identity.accounts")}>
                <Badge variant="secondary">
                  {t("company.insights.identity.confidentialAccounts")}
                </Badge>
              </IdentityField>
            )}

            {hasCollectiveAgreements && (
              <IdentityField
                label={t("company.insights.identity.collectiveAgreement")}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  {business.collectiveAgreements!.map((agreement) => (
                    <Tooltip key={agreement.idcc}>
                      <TooltipTrigger asChild>
                        <Badge variant="secondary" className="cursor-pointer">
                          IDCC {agreement.idcc}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {agreement.name}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </IdentityField>
            )}

            {hasCorporateObject && (
              <IdentityField
                label={t("company.insights.identity.corporateObject")}
                spanFull
              >
                <CorporateObject text={business.corporateObject!} />
              </IdentityField>
            )}
          </div>
        )}

        {hasCertifications && (
          <>
            {showIdentityGrid && <Separator />}
            <div className="flex flex-col gap-2">
              <FieldLabel>
                {t("company.insights.certifications.title")}
              </FieldLabel>
              <div className="flex flex-wrap gap-1.5">
                {certificationBadges.map((badge) => (
                  <Badge key={badge} variant="secondary">
                    {badge}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
